import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";
import { BrainCard } from "@/components/admin/brain/BrainCard";
import { useBrainDeck } from "@/hooks/brain/useBrainDeck";
import {
  filterDeck,
  resolveReturnIndex,
  withBrainReturn,
} from "@/lib/brain/deck";
import { KIND_META } from "@/lib/brain/kind-meta";
import {
  BRAIN_CATEGORIES,
  type BrainCategory,
  type BrainScope,
} from "@/lib/brain/types";

const LAST_INDEX = "brain.lastIndex";
/** The filters in use, so "Back to colivIQ" returns to the same view. */
const VIEW = "brain.view";
const SCOPES: readonly BrainScope[] = ["all", "mine", "everyone"];
const KBD =
  "rounded border bg-background px-1.5 py-0.5 font-sans text-[11px] text-foreground";

/** Typing fields and open popups own every key. */
const OWNS_KEYS =
  'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="menu"], [role="listbox"], [role="dialog"], [role="alertdialog"]';
/** Controls whose own Enter means something (click, follow, open). */
const OWNS_ENTER = 'a, button, [role="button"], [role="combobox"]';

function readLastIndex(): number {
  try {
    return Number(sessionStorage.getItem(LAST_INDEX) ?? 0) || 0;
  } catch {
    return 0;
  }
}

function readView(): { category: BrainCategory | "all"; scope: BrainScope } {
  try {
    const v = JSON.parse(sessionStorage.getItem(VIEW) ?? "{}");
    return {
      category: (["all", ...BRAIN_CATEGORIES] as string[]).includes(v?.category)
        ? v.category
        : "all",
      scope: (SCOPES as string[]).includes(v?.scope) ? v.scope : "all",
    };
  } catch {
    return { category: "all", scope: "all" };
  }
}

export default function BrainPage() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { deck, isLoading, state, me, failedCategories, refresh } =
    useBrainDeck(true);
  // Coming back from a deep link: re-read every source so a card resolved
  // there is gone, not shown again until the next poll.
  useEffect(() => {
    void refresh();
  }, [refresh]);
  // A source failed to load: never claim the list (or a view) is clear.
  const incomplete = failedCategories.length > 0;
  const [category, setCategory] = useState(() => readView().category);
  const [scope, setScope] = useState(() => readView().scope);
  useEffect(() => {
    try {
      sessionStorage.setItem(VIEW, JSON.stringify({ category, scope }));
    } catch {
      /* private mode: the view memory is a convenience */
    }
  }, [category, scope]);
  // Free-text search across every tab (COL-593 item 15): the card's kind, the
  // person/place it names, its one-line summary and its fact values — so an
  // owner or apartment named in a setup-gap fact is findable by name.
  const [query, setQuery] = useState("");
  const cards = useMemo(() => {
    const base = filterDeck(deck.cards, category, scope, me);
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter((c) =>
      [
        t(KIND_META[c.kind].title.key, KIND_META[c.kind].title.fallback),
        c.who?.label,
        c.where?.label,
        c.summary,
        ...c.facts.map((f) => f.value),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [deck.cards, category, scope, me, query, t]);
  // Chips count what the scope shows, so "Mine" never reads "All 129" over "1 / 2".
  const counts = useMemo(() => {
    const scoped = filterDeck(deck.cards, "all", scope, me);
    const by = (c: BrainCategory) => scoped.filter((x) => x.category === c);
    return {
      all: scoped.length,
      ...Object.fromEntries(BRAIN_CATEGORIES.map((c) => [c, by(c).length])),
    } as Record<BrainCategory | "all", number>;
  }, [deck.cards, scope, me]);
  // The card on screen is followed by key: a re-sort (a priority change, an
  // assignment, a background reload) moves it and the deck moves with it, still
  // mounted with whatever was typed in it. Once it leaves the list (resolved,
  // acknowledged, assigned away), the next card slides into its slot.
  const [sel, setSel] = useState<{ key: string | null; slot: number }>(() => ({
    key: null,
    slot: readLastIndex(),
  }));
  const followed = sel.key ? cards.findIndex((c) => c.key === sel.key) : -1;
  // Clamped on read: the list shrinks under us as cards get resolved.
  const pos =
    followed >= 0
      ? followed
      : Math.max(0, Math.min(sel.slot, cards.length - 1));
  const current = cards[pos];
  // A carousel: past the last card is the first, before the first the last.
  const go = (i: number) => {
    const n = cards.length;
    const slot = n ? ((i % n) + n) % n : 0;
    setSel({ key: cards[slot]?.key ?? null, slot });
  };
  const restart = () => setSel({ key: null, slot: 0 });

  // The first complete load. After it, a source that re-keys (a new booking,
  // a resolved card) loads in the background: the open card stays mounted —
  // with whatever was typed in it — and the position stays where it is.
  const [settled, setSettled] = useState(false);
  // Land on the card we left (by key), else the same slot: once, on that load.
  const returnKey = params.get("item");
  useEffect(() => {
    if (isLoading || settled) return;
    setSettled(true);
    go(resolveReturnIndex(cards, returnKey, readLastIndex()));
    if (returnKey) {
      params.delete("item");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Follow whatever is on screen: the followed card's latest slot, or the card
  // that took the slot once it left. Not before the first load lands it.
  const shownKey = current?.key;
  useEffect(() => {
    if (!settled || !shownKey) return;
    if (shownKey !== sel.key || pos !== sel.slot)
      setSel({ key: shownKey, slot: pos });
  }, [settled, shownKey, pos, sel.key, sel.slot]);

  useEffect(() => {
    // An empty or not-yet-loaded list says nothing about where we were.
    if (!settled || !cards.length) return;
    try {
      sessionStorage.setItem(LAST_INDEX, String(pos));
    } catch {
      /* private mode: position memory is a convenience */
    }
  }, [pos, settled, cards.length]);

  const run = async (fn: () => Promise<void>) => {
    try {
      await fn();
    } catch (e) {
      toast.error(
        (e as { message?: string } | null)?.message ||
          t("brain.error.generic", "Something went wrong")
      );
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Handled already (a focused Radix trigger, the note field), or a held
      // key: never act twice.
      if (e.defaultPrevented || e.repeat) return;
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const el = e.target instanceof Element ? e.target : null;
      if (el?.closest(OWNS_KEYS)) return;
      if (e.key === "ArrowRight") go(pos + 1);
      else if (e.key === "ArrowLeft") go(pos - 1);
      else if (e.key === "Enter" && current) {
        if (e.shiftKey) {
          e.preventDefault();
          void run(() => state.acknowledge(current.key, null));
        } else if (!el?.closest(OWNS_ENTER)) {
          e.preventDefault();
          navigate(withBrainReturn(current.primaryHref, current.key));
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Today's acknowledged cards, to bring back: under the card on request, and
  // always in the empty view.
  const [showAcked, setShowAcked] = useState(false);
  const acknowledgedList = (
    <>
      <p className="text-sm font-medium text-foreground">
        {t(
          "brain.empty.backTomorrow",
          "Acknowledged today — back tomorrow if still open"
        )}
      </p>
      <ul className="mt-2 space-y-1 text-sm text-foreground">
        {deck.acknowledged.map((c) => (
          <li key={c.key} className="flex items-center justify-between gap-2">
            <span>
              {t(KIND_META[c.kind].title.key, KIND_META[c.kind].title.fallback)}
              {c.who ? ` · ${c.who.label}` : ""}
              {c.ackNote ? ` — ${c.ackNote}` : ""}
            </span>
            <Button
              variant="link"
              size="sm"
              onClick={() => void run(() => state.unacknowledge(c.key))}
            >
              {t("brain.ack.undo", "Bring back")}
            </Button>
          </li>
        ))}
      </ul>
    </>
  );

  const today = new Intl.DateTimeFormat(language === "de" ? "de-DE" : "en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "Europe/Berlin", // the same day acknowledgements are keyed to
  }).format(new Date());

  return (
    // The nav already names the page, so the heading is the day this deck is
    // for. Heading, filters and card share one column so their edges line up.
    <AdminV2Layout>
      <div className="mx-auto w-full max-w-3xl">
        <header className="mb-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              <span className="sr-only">
                {t("brain.page.title", "colivIQ")}:{" "}
              </span>
              {today}
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {deck.counts.all}{" "}
              {incomplete
                ? t("brain.page.openIncomplete", "open (incomplete)")
                : t("brain.page.open", "open")}
            </p>
          </div>
          <div className="flex flex-col items-stretch gap-2 sm:items-end">
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                restart();
              }}
              placeholder={t("brain.search.placeholder", "Search cards")}
              aria-label={t("brain.search.label", "Search cards")}
              className="h-8 w-full sm:w-56"
            />
            <div className="flex gap-1">
              {SCOPES.map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={scope === s ? "secondary" : "ghost"}
                  aria-pressed={scope === s}
                  onClick={() => {
                    setScope(s);
                    restart();
                  }}
                >
                  {t(
                    `brain.scope.${s}`,
                    s === "all" ? "All" : s === "mine" ? "Mine" : "Unassigned"
                  )}
                </Button>
              ))}
            </div>
          </div>
        </header>

        <div className="mb-6 flex flex-wrap items-center gap-2">
          {(["all", ...BRAIN_CATEGORIES] as const).map((c) => (
            <Button
              key={c}
              size="sm"
              variant={category === c ? "default" : "outline"}
              aria-pressed={category === c}
              onClick={() => {
                setCategory(c);
                restart();
              }}
            >
              {t(
                `brain.category.${c}`,
                c === "all" ? "All" : c[0].toUpperCase() + c.slice(1)
              )}
              <span className="ml-1.5 tabular-nums opacity-70">
                {counts[c]}
              </span>
              {/* A failed source: this count is a lower bound, not the truth.
                The banner below says so; the chip stays a plain number. */}
              {c !== "all" && failedCategories.includes(c) && (
                <span className="sr-only">
                  {t(
                    "brain.error.categoryIncomplete",
                    "Couldn't load — may be incomplete"
                  )}
                </span>
              )}
            </Button>
          ))}
        </div>

        {incomplete && (
          <Alert className="mb-6 border-amber-300 bg-amber-50 text-amber-900">
            <AlertDescription>
              {t(
                "brain.error.partialNamed",
                "{categories} couldn't load — this list may be incomplete."
              ).replace(
                "{categories}",
                failedCategories
                  .map((c) =>
                    t(`brain.category.${c}`, c[0].toUpperCase() + c.slice(1))
                  )
                  .join(", ")
              )}
            </AlertDescription>
          </Alert>
        )}

        {isLoading && !(settled && current) ? (
          <div role="status" aria-live="polite" className="space-y-3">
            <Card className="w-full space-y-3 p-5">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-16 w-full" />
            </Card>
            <p className="text-center text-sm text-muted-foreground">
              {t("brain.loadingDeck", "Gathering today's cards…")}
            </p>
          </div>
        ) : current ? (
          <>
            <BrainCard
              key={current.key}
              card={current}
              position={pos}
              total={cards.length}
              me={me}
              onPrev={() => go(pos - 1)}
              onNext={() => go(pos + 1)}
              onAcknowledge={(note) =>
                void run(() => state.acknowledge(current.key, note))
              }
              onAssign={(id, note) =>
                void run(() => state.assign(current.key, id, note))
              }
              notes={state.notes.get(current.key)}
              onAddNote={(note) =>
                void run(() => state.addNote(current.key, note))
              }
              onPriority={(level) =>
                void run(() => state.setPriority(current.key, level))
              }
              // Stay in this slot: the next card slides in when the resolved one drops out.
              onDone={() => go(pos)}
            />
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
              {/* No keyboard on a phone: the hints only take room there. Key caps
                are key names, not copy: the same in every language. */}
              <p className="hidden items-center gap-1.5 sm:flex">
                <kbd className={KBD}>←</kbd>
                <kbd className={KBD}>→</kbd>
                <span className="mr-2">{t("brain.keys.browse", "browse")}</span>
                <kbd className={KBD}>{"Enter"}</kbd>
                <span className="mr-2">{t("brain.keys.open", "open")}</span>
                <kbd className={KBD}>{"⇧ Enter"}</kbd>
                <span>{t("brain.keys.ack", "acknowledge")}</span>
              </p>
              {deck.acknowledged.length > 0 && (
                <Button
                  variant="link"
                  size="sm"
                  className="ml-auto h-auto p-0 text-xs"
                  aria-expanded={showAcked}
                  onClick={() => setShowAcked((v) => !v)}
                >
                  {t(
                    "brain.ack.todayToggle",
                    "Acknowledged today ({n})"
                  ).replace("{n}", String(deck.acknowledged.length))}
                </Button>
              )}
            </div>
            {showAcked && deck.acknowledged.length > 0 && (
              <div className="mt-3">{acknowledgedList}</div>
            )}
          </>
        ) : (
          <div className="mx-auto max-w-[560px] text-center">
            {deck.cards.length > 0 ? (
              <>
                {!incomplete && (
                  <p className="text-lg font-semibold">
                    {t("brain.empty.filtered", "Nothing open in this view")}
                  </p>
                )}
                <Button
                  variant="link"
                  onClick={() => {
                    setCategory("all");
                    setScope("all");
                    setQuery("");
                    restart();
                  }}
                >
                  {t("brain.empty.showAll", "Show all open cards")}
                </Button>
              </>
            ) : incomplete ? null : (
              <>
                <p className="text-lg font-semibold">
                  {t("brain.empty.title", "Nothing open")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "brain.empty.body",
                    "Everything that needed a decision today has one."
                  )}
                </p>
              </>
            )}
            {deck.acknowledged.length > 0 && (
              <div className="mt-6 text-left">{acknowledgedList}</div>
            )}
          </div>
        )}
      </div>
    </AdminV2Layout>
  );
}
